import { useCurrentUser, type CurrentUser } from "./use-current-user";

/**
 * Keys of the RBAC matrix (docs/architecture/RBAC.md), mirroring the backend `Permission` enum. They
 * are spelled out here rather than derived from the generated schema because OpenAPI describes the
 * field as a plain string array — a typo would otherwise only show up as a silently hidden button.
 */
export const permissions = [
  "tenant.settings.read",
  "tenant.settings.write",
  "tenant.colleagues.read",
  "tenant.users.read",
  "tenant.users.manage",
  "billing.manage",
  "inventory.read",
  "inventory.write",
  "purchasing.manage",
  "catalog.read",
  "catalog.write",
  "production.read",
  "production.write",
  "roastprofile.read",
  "roastprofile.write",
  "roastprofile.manage",
  "quality.read",
  "quality.write",
  "sales.read",
  "sales.write",
  "sales.ship",
  "sales.payments.write",
  "sales.credit.override",
  "integration.read",
  "integration.manage",
  "reports.read",
  "reports.close",
  "labels.print",
  "labels.manage",
  "planning.read",
  "planning.write",
  "crm.read",
  "crm.write",
  "compliance.read",
  "compliance.write",
  "compliance.manage",
  "services.read",
  "services.write",
  "feedback.submit",
  "feedback.read.all",
  "portal.orders.read",
  "portal.orders.write",
  "platform.tenants.manage",
  "platform.plans.manage",
  "platform.support.manage",
  "platform.insights.read",
  "platform.operations.manage",
] as const;

export type Permission = (typeof permissions)[number];

/**
 * Whether the user holds every one of the given permissions. The backend decides — this reads the
 * list it returned from `GET /api/v1/me`; it never re-derives permissions from roles in the browser.
 */
export function can(user: CurrentUser | undefined, ...required: Permission[]): boolean {
  if (!user?.permissions || required.length === 0) {
    return false;
  }
  const held: string[] = user.permissions;
  return required.every((permission) => held.includes(permission));
}

/**
 * UI half of the RBAC matrix: `useCan("inventory.write")` hides what the API would refuse anyway.
 * Hiding is a courtesy, not a control — the guard that matters is `@RequiresPermission` on the server.
 *
 * Returns a plain boolean, false while `GET /api/v1/me` is still in flight: hide first, reveal once
 * the answer is in. A `{ allowed, isLoading }` object would read as truthy in `if (useCan(...))`,
 * which is exactly the mistake that silently grants everyone everything.
 */
export function useCan(...required: Permission[]): boolean {
  const { user, isLoading } = useCurrentUser();
  return !isLoading && can(user, ...required);
}
